import React, { FC, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ColumnConfig, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { SnowflakeDataSharingService } from "@app/operations/snowflake-data-sharing/snowflakeDataSharingService";
import { BatchProjectStatus } from "@app/operations/snowflake-data-sharing/interfaces";

interface BatchProjectStatusesWaffleToppings {
  key: string;
  networkAdequacySummaryErrorMessage: string;
  networkAdequacySummaryRowCount: string;
  networkAdequacySummaryStatus: string;
  projectId: number;
  projectName: string;
  projectRunConfigurationId: number;
  servicingProviderErrorMessage: string;
  servicingProviderRowCount: string;
  servicingProviderStatus: string;
}

const batchProjectStatusesColumnKeys = {
  networkAdequacySummaryErrorMessage: "networkAdequacySummaryErrorMessage",
  networkAdequacySummaryRowCount: "networkAdequacySummaryRowCount",
  networkAdequacySummaryStatus: "networkAdequacySummaryStatus",
  projectId: "projectId",
  projectName: "projectName",
  projectRunConfigurationId: "projectRunConfigurationId",
  servicingProviderErrorMessage: "servicingProviderErrorMessage",
  servicingProviderRowCount: "servicingProviderRowCount",
  servicingProviderStatus: "servicingProviderStatus",
} as const satisfies Record<string, keyof BatchProjectStatusesWaffleToppings>;

const buildBatchProjectStatusesWaffleData = (projects: BatchProjectStatus[]): BatchProjectStatusesWaffleToppings[] => {
  return projects.map<BatchProjectStatusesWaffleToppings>((project) => {
    /* oxlint-disable sort-keys */
    return {
      projectName: project.projectName,
      projectId: project.projectId,
      projectRunConfigurationId: project.projectRunConfigurationId,
      networkAdequacySummaryRowCount: project.networkAdequacySummaryRowCount?.toString() ?? "—",
      networkAdequacySummaryStatus: project.networkAdequacySummaryStatus ?? "—",
      networkAdequacySummaryErrorMessage: project.networkAdequacySummaryErrorMessage ?? "—",
      servicingProviderRowCount: project.servicingProviderRowCount?.toString() ?? "—",
      servicingProviderStatus: project.servicingProviderStatus ?? "—",
      servicingProviderErrorMessage: project.servicingProviderErrorMessage ?? "—",
      [wafflesKeyColumnName]: project.projectRunConfigurationId.toString(),
    };
    /* oxlint-enable sort-keys */
  });
};

const batchProjectStatusesColumnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  [batchProjectStatusesColumnKeys.projectName]: { displayText: "Project Name", testId: "batch-project-status-project-name" },
  [batchProjectStatusesColumnKeys.projectId]: { displayText: "Project ID", testId: "batch-project-status-project-id" },
  [batchProjectStatusesColumnKeys.projectRunConfigurationId]: { displayText: "Run Configuration ID", testId: "batch-project-status-run-configuration-id" },
  [batchProjectStatusesColumnKeys.networkAdequacySummaryRowCount]: { displayText: "Network Adequacy Summary Rows Inserted", testId: "batch-project-status-nas-row-count" },
  [batchProjectStatusesColumnKeys.networkAdequacySummaryStatus]: { displayText: "Network Adequacy Summary Status", testId: "batch-project-status-nas-status" },
  [batchProjectStatusesColumnKeys.networkAdequacySummaryErrorMessage]: { displayText: "Network Adequacy Summary Error", testId: "batch-project-status-nas-error" },
  [batchProjectStatusesColumnKeys.servicingProviderRowCount]: { displayText: "Servicing Provider Rows Inserted", testId: "batch-project-status-sp-row-count" },
  [batchProjectStatusesColumnKeys.servicingProviderStatus]: { displayText: "Servicing Provider Status", testId: "batch-project-status-sp-status" },
  [batchProjectStatusesColumnKeys.servicingProviderErrorMessage]: { displayText: "Servicing Provider Error", testId: "batch-project-status-sp-error" },
  /* oxlint-enable sort-keys */
};

const batchProjectStatusesPageSize = 25;

interface BatchProjectStatusesFlyoutBodyProps {
  batchId: string;
  clientId: number;
}

export const BatchProjectStatusesFlyoutBody: FC<BatchProjectStatusesFlyoutBodyProps> = (props: BatchProjectStatusesFlyoutBodyProps) => {
  const dataSharingService = useRef(new SnowflakeDataSharingService()).current;
  const [skip, setSkip] = useState<number>(0);

  const batchProjectStatusesQuery = useQuery({
    queryFn: ({ signal }) => dataSharingService.getBatchProjectStatuses(props.clientId, props.batchId, signal),
    queryKey: ["snowflake-data-sharing-batch-project-statuses", props.clientId, props.batchId],
    retry: false,
  });

  const tableData = useMemo(
    () => buildBatchProjectStatusesWaffleData(batchProjectStatusesQuery.data ?? []),
    [batchProjectStatusesQuery.data],
  );

  if (batchProjectStatusesQuery.isLoading) {
    return (
      <div className={"d-flex justify-content-center"}>
        <LoadingSpinner />
      </div>
    );
  }

  if (batchProjectStatusesQuery.isError) {
    return (
      <QESAlert type="danger" persist={true}>
        There was an error loading the projects for this batch. This client may not have a Snowflake outbound database configured, or another issue occurred.
      </QESAlert>
    );
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber) => setSkip((pageNumber - 1) * batchProjectStatusesPageSize),
    recordsPerPage: batchProjectStatusesPageSize,
    skip,
    totalRecords: tableData.length,
  };

  return (
    <Waffles<BatchProjectStatusesWaffleToppings>
      columns={batchProjectStatusesColumnConfig}
      data={tableData.slice(skip, skip + batchProjectStatusesPageSize)}
      enablePagination={true}
      fullWidth={true}
      paginationProps={paginationProps}
      testId="snowflake-data-sharing-batch-project-statuses"
    />
  );
};
